import { Link } from 'react-router';
import type { MonitoringAlert } from '@bekebi/contracts/api';
import { Button } from '../../components/button';
import { CheckCircleIcon, HistoryIcon } from '../../components/icons';
import { ErrorState, LoadingState } from '../../components/states';
import { ServiceTypeIcon } from '../services/components';
import { formatDateTime, formatRelative } from '../services/format';
import { ALERT_REASON_TEXT, AlertStateBadge, formatDuration } from './alert-present';
import { useAlerts } from './api';
import { MonitoringHeader } from './monitoring-header';
import { NotificationsSection } from './notifications-section';

/**
 * Alerts (/app/monitoring/alerts, Admin, `monitoring.view`): open alerts, then resolved ones, from
 * GET /api/monitoring/alerts. Alerts are opened and resolved by the monitoring worker from each
 * service's derived health; there is nothing here to open, change or resolve by hand. Below them,
 * the alert emails (Phase 07 Stage 05-C).
 */
export function AlertsOverview() {
  return (
    <div className="max-w-6xl">
      <MonitoringHeader current="alerts">
        An alert opens automatically when a service needs attention, and is resolved when the
        service&rsquo;s checks pass again. Both come from the monitoring worker&rsquo;s check
        results &mdash; nobody opens or resolves an alert by hand.
      </MonitoringHeader>
      <div className="mt-6 space-y-6">
        <AlertList state="open" />
        <AlertList state="resolved" />
        <NotificationsSection />
      </div>
    </div>
  );
}

const LISTS = {
  open: {
    title: 'Open alerts',
    loading: 'Loading open alerts…',
    error: 'Open alerts could not be loaded',
    more: 'Show more open alerts',
  },
  resolved: {
    title: 'Resolved alerts',
    loading: 'Loading resolved alerts…',
    error: 'Resolved alerts could not be loaded',
    more: 'Show older alerts',
  },
} as const;

function AlertList({ state }: { state: MonitoringAlert['state'] }) {
  const alerts = useAlerts(state);
  const text = LISTS[state];
  const items = alerts.data?.pages.flatMap((p) => p.items) ?? [];
  const id = `alerts-${state}`;

  return (
    <section
      aria-labelledby={id}
      className="rounded-[var(--radius-card)] border border-line bg-surface p-5 sm:p-6"
    >
      <div className="flex items-center justify-between gap-3">
        <h2 id={id}>{text.title}</h2>
        {state === 'open' && items.length > 0 ? (
          <span className="rounded-full bg-danger-soft px-2.5 py-0.5 text-xs font-semibold text-danger">
            {items.length}
            {alerts.hasNextPage ? '+' : ''}
          </span>
        ) : null}
      </div>
      <div className="mt-4">
        {alerts.isPending ? (
          <LoadingState label={text.loading} />
        ) : alerts.isError ? (
          <ErrorState
            title={text.error}
            message={alerts.error.message}
            onRetry={() => void alerts.refetch()}
          />
        ) : items.length === 0 ? (
          state === 'open' ? (
            <div className="flex items-center gap-3 rounded-xl bg-success-soft/60 px-4 py-3 text-sm">
              <CheckCircleIcon className="size-5 shrink-0 text-success" />
              <p className="text-fg-2">
                <span className="font-semibold text-fg">No open alerts.</span> No service needs
                attention right now.
              </p>
            </div>
          ) : (
            <div className="flex items-center gap-3 rounded-xl border border-dashed border-line px-4 py-3 text-sm text-muted">
              <HistoryIcon className="size-5 shrink-0" />
              <p>No resolved alerts yet. An alert is resolved when its service has recovered.</p>
            </div>
          )
        ) : (
          <>
            <ul aria-label={text.title} className="space-y-2">
              {items.map((alert) => (
                <li key={alert.id}>
                  <AlertRow alert={alert} />
                </li>
              ))}
            </ul>
            {alerts.hasNextPage ? (
              <Button
                variant="secondary"
                className="mt-4"
                onClick={() => void alerts.fetchNextPage()}
                disabled={alerts.isFetchingNextPage}
              >
                {alerts.isFetchingNextPage ? 'Loading…' : text.more}
              </Button>
            ) : null}
            {alerts.isFetchNextPageError ? (
              <p role="alert" className="mt-3 text-sm text-danger">
                More alerts could not be loaded. Try again.
              </p>
            ) : null}
          </>
        )}
      </div>
    </section>
  );
}

function AlertRow({ alert }: { alert: MonitoringAlert }) {
  return (
    <article className="group relative flex gap-3 rounded-xl border border-line bg-canvas/40 p-4 text-sm transition-colors hover:border-line-strong hover:bg-raised/40">
      <ServiceTypeIcon type={alert.service.type} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
          <h3 className="font-semibold">
            <Link
              to={`/app/monitoring/alerts/${alert.id}`}
              className="text-fg after:absolute after:inset-0 after:rounded-xl group-hover:text-accent-link"
            >
              {alert.service.name}
            </Link>
          </h3>
          <AlertStateBadge alert={alert} />
        </div>
        <p className="mt-0.5 text-fg-2">{ALERT_REASON_TEXT[alert.reason]}</p>
        <p className="mt-2 text-xs text-muted">
          {alert.resolvedAt ? (
            <>
              Resolved <time dateTime={alert.resolvedAt}>{formatDateTime(alert.resolvedAt)}</time> ·
              lasted {formatDuration(alert.openedAt, alert.resolvedAt)}
            </>
          ) : (
            <>
              Opened <time dateTime={alert.openedAt}>{formatRelative(alert.openedAt)}</time> ·{' '}
              {formatDateTime(alert.openedAt)}
            </>
          )}
        </p>
      </div>
    </article>
  );
}
